import { Badge } from "@arcadeai/design-system/components/ui/atoms/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@arcadeai/design-system/components/ui/atoms/tooltip";
import {
  OAUTH_PROVIDER_CATALOGUE,
  OAUTH2_PROVIDER,
} from "@arcadeai/design-system/metadata/oauth-providers";
import type { ColumnDef } from "@tanstack/react-table";
import { Layers2 } from "lucide-react";
import type { OAuthProviderItem } from "@/app/oauth/types";
import { DataTableColumnHeader } from "@/components/custom/table/data-table-column-header";

const CLIENT_ID_DISPLAY_LENGTH = 20;

import { getRelativeTimeString } from "@/lib/dates";
import { OAuthActions } from "./oauth-actions";

export const createOAuthColumns = (
  overriddenProviders?: Set<string>,
  overridingProviders?: Set<string>
) => {
  const columns: ColumnDef<OAuthProviderItem>[] = [
    {
      id: "provider",
      accessorKey: "provider_id",
      meta: {
        displayName: "Provider",
        defaultVisible: true,
      },
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Provider" />
      ),
      cell: ({ row }) => {
        const providerId = row.original.provider_id;
        const provider = row.original;
        // overridden/overriding are based on provider_id comparisons across bindings
        const isOverridden = overriddenProviders?.has(provider.provider_id);
        const isOverriding = overridingProviders?.has(provider.provider_id);
        const descriptor =
          OAUTH_PROVIDER_CATALOGUE[providerId] ?? OAUTH2_PROVIDER;

        const isCustomOAuth = descriptor === OAUTH2_PROVIDER;

        return (
          <div
            className={`group flex items-start gap-3 py-1 transition-opacity duration-200 ${
              isOverridden ? "opacity-50" : ""
            }`}
          >
            <div
              className={`relative flex-shrink-0 rounded-md p-1.5 transition-all duration-200 ${
                isOverridden
                  ? "bg-muted/30"
                  : "bg-muted/50 group-hover:bg-muted/50"
              }`}
            >
              <descriptor.icon className="size-6" />
              {!!isOverridden && (
                <div className="absolute -top-1 -right-1 size-2.5 rounded-full border border-background bg-orange-500" />
              )}
            </div>
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              <div className="flex items-center gap-2">
                {!!isOverridden && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <Layers2 className="h-3.5 w-3.5 text-orange-500" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Overridden by a project OAuth provider</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
                {!!isOverriding && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <Layers2 className="h-3.5 w-3.5 text-green-500" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Overrides organization or global OAuth provider</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
                <span
                  className={`font-medium text-sm leading-tight ${
                    isOverridden ? "text-muted-foreground line-through" : ""
                  }`}
                >
                  {descriptor.name}
                </span>
                {!!isCustomOAuth && (
                  <Badge className="h-4 px-1.5 py-0 text-xs" variant="outline">
                    Custom
                  </Badge>
                )}
              </div>
              {!!descriptor.description && (
                <p
                  className={`line-clamp-2 text-muted-foreground text-xs leading-tight ${
                    isOverridden ? "opacity-70" : ""
                  }`}
                >
                  {descriptor.description}
                </p>
              )}
            </div>
          </div>
        );
      },
    },

    {
      id: "id",
      accessorKey: "id",
      meta: {
        displayName: "ID",
        defaultVisible: true,
      },
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="ID" />
      ),
      cell: ({ row }) => {
        const id = row.original.id;
        const providerId = row.original.provider_id;
        const isOverridden = overriddenProviders?.has(providerId);

        return (
          <span
            className={isOverridden ? "text-muted-foreground line-through" : ""}
          >
            {id}
          </span>
        );
      },
    },

    {
      id: "description",
      accessorKey: "description",
      meta: {
        displayName: "Description",
        defaultVisible: true,
      },
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Description" />
      ),
      cell: ({ row }) => {
        const description = row.original.description;
        const isOverridden = overriddenProviders?.has(row.original.provider_id);

        return (
          <span className={`text-sm ${isOverridden ? "opacity-60" : ""}`}>
            {description || "No description provided"}
          </span>
        );
      },
    },

    {
      id: "status",
      accessorKey: "status",
      meta: {
        displayName: "Status",
        defaultVisible: true,
      },
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Status" />
      ),
      cell: ({ row }) => {
        const status = row.original.status;
        const isOverridden = overriddenProviders?.has(row.original.provider_id);
        const isActive = status === "active";

        return (
          <Badge
            className={isOverridden ? "opacity-60" : ""}
            variant={isActive ? "success" : "secondary"}
          >
            {isActive ? "Active" : "Inactive"}
          </Badge>
        );
      },
    },

    {
      id: "client_id",
      accessorKey: "oauth2.client_id",
      enableHiding: true,
      meta: {
        displayName: "Client ID",
        defaultVisible: false,
      },
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Client ID" />
      ),
      cell: ({ row }) => {
        const clientId = row.original.oauth2.client_id;
        const isOverridden = overriddenProviders?.has(row.original.provider_id);

        if (!clientId) {
          return (
            <span
              className={`text-muted-foreground ${isOverridden ? "opacity-60" : ""}`}
            >
              -
            </span>
          );
        }

        return (
          <code
            className={`rounded-md bg-muted px-1.5 py-0.5 font-mono text-xs ${isOverridden ? "opacity-60" : ""}`}
          >
            {clientId.length > CLIENT_ID_DISPLAY_LENGTH
              ? `${clientId.slice(0, CLIENT_ID_DISPLAY_LENGTH)}...`
              : clientId || ""}
          </code>
        );
      },
    },

    {
      id: "created_at",
      accessorKey: "created_at",
      meta: {
        displayName: "Created At",
        defaultVisible: false,
      },
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Created At" />
      ),
      cell: ({ row }) => {
        const isOverridden = overriddenProviders?.has(row.original.provider_id);

        if (
          !row.original.created_at ||
          row.original.created_at === "0001-01-01T00:00:00Z"
        ) {
          return <span className={isOverridden ? "opacity-60" : ""}>-</span>;
        }

        const createdAt = new Date(row.original.created_at);
        return (
          <time
            className={isOverridden ? "opacity-60" : ""}
            dateTime={createdAt.toISOString()}
            title={createdAt.toUTCString()}
          >
            {getRelativeTimeString(createdAt)}
          </time>
        );
      },
    },

    {
      id: "updated_at",
      accessorKey: "updated_at",
      enableHiding: true,
      meta: {
        displayName: "Updated At",
        defaultVisible: false,
      },
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Updated At" />
      ),
      cell: ({ row }) => {
        const isOverridden = overriddenProviders?.has(row.original.provider_id);

        if (
          !row.original.updated_at ||
          row.original.updated_at === "0001-01-01T00:00:00Z"
        ) {
          return <span className={isOverridden ? "opacity-60" : ""}>-</span>;
        }

        const updatedAt = new Date(row.original.updated_at);
        return (
          <time
            className={isOverridden ? "opacity-60" : ""}
            dateTime={updatedAt.toISOString()}
            title={updatedAt.toUTCString()}
          >
            {getRelativeTimeString(updatedAt)}
          </time>
        );
      },
    },

    {
      id: "actions",
      enableHiding: false,
      meta: {
        displayName: "Actions",
        defaultVisible: true,
      },
      header: () => <div className="flex items-center space-x-2">Actions</div>,
      cell: ({ row }) => <OAuthActions oauth={row.original} />,
    },
  ];

  return columns;
};
